import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import { useMaintenanceStatus } from "@/hooks/useMaintenanceStatus";
import { isPlatformRole } from "@/types/firestore";

export function MaintenanceBanner() {
  const { orgUser, role, realRole } = useAuth();
  const maintenance = useMaintenanceStatus(realRole, orgUser?.orgId);
  const [ending, setEnding] = useState(false);

  if (maintenance.loading) return null;

  const isPlatform = realRole ? isPlatformRole(realRole) : false;

  // Show platform maintenance banner to platform admins
  if (maintenance.platformActive && isPlatform) {
    return (
      <BannerBar
        text="Platform maintenance is active. All non-admin users are locked out."
        onEnd={async () => {
          setEnding(true);
          try {
            const toggle = httpsCallable(functions, "toggleMaintenance");
            await toggle({ scope: "platform", activate: false });
          } finally {
            setEnding(false);
          }
        }}
        ending={ending}
      />
    );
  }

  // Show org maintenance banner to org admins (ORG_OWNER/ORG_ADMIN)
  if (maintenance.orgActive && !isPlatform && (role === "ORG_OWNER" || role === "ORG_ADMIN")) {
    return (
      <BannerBar
        text={`${maintenance.orgName ?? "Organization"} maintenance is active. Users see a maintenance page.`}
        onEnd={async () => {
          setEnding(true);
          try {
            const toggle = httpsCallable(functions, "toggleMaintenance");
            await toggle({ scope: "org", orgId: orgUser?.orgId, activate: false });
          } finally {
            setEnding(false);
          }
        }}
        ending={ending}
      />
    );
  }

  return null;
}

function BannerBar({
  text,
  onEnd,
  ending,
}: {
  text: string;
  onEnd: () => void;
  ending: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 bg-amber-50 border-b border-amber-200 px-4 py-2">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
        </span>
        <span className="font-body text-sm text-amber-800">{text}</span>
      </div>
      <button
        onClick={onEnd}
        disabled={ending}
        className="rounded-md bg-amber-600 px-3 py-1 font-heading text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
      >
        {ending ? "Ending..." : "End Maintenance"}
      </button>
    </div>
  );
}
